/**
 * Client-side video pre-compression before upload.
 *
 * We decode the provider's source clip into a hidden <video>, draw its frames
 * onto a Canvas scaled down to 720p (longest edge), capture the canvas stream
 * plus the original audio track, and feed them through MediaRecorder at a
 * target ~2.5 Mbps. The output is a Blob the caller can drop straight into
 * a FormData upload.
 *
 * The server will transcode again to standardise format + enforce bitrate,
 * so the output codec here doesn't matter for playback; what matters is
 * getting the upload over the wire fast. Typical 1080p phone clips drop
 * from 20-40 MB to 3-8 MB in this pass, cutting upload time 5-10x on 4G.
 *
 * Falls back silently to the original file if the browser doesn't support
 * the bits we need (very old Safari, off-brand Android). The server can
 * always cope with a large raw upload.
 */

const TARGET_LONG_EDGE = 1280;      // "720p" when one edge is 720
const TARGET_BITRATE = 2_500_000;   // 2.5 Mbps
const SIZE_THRESHOLD_BYTES = 8 * 1024 * 1024; // only compress files >8MB

// iOS Safari supports MP4 output since 14.5, elsewhere WebM is universal.
const PREFERRED_MIME_TYPES = [
  'video/mp4;codecs=avc1',
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
];

function pickSupportedMime() {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const mime of PREFERRED_MIME_TYPES) {
    try {
      if (MediaRecorder.isTypeSupported(mime)) return mime;
    } catch { /* noop */ }
  }
  return null;
}

function mimeToExtension(mime) {
  if (!mime) return 'webm';
  if (mime.includes('mp4')) return 'mp4';
  return 'webm';
}

async function waitForVideoReady(video) {
  return new Promise((resolve, reject) => {
    if (video.readyState >= 1 && video.duration) { resolve(); return; }
    const onLoaded = () => { cleanup(); resolve(); };
    const onError = () => { cleanup(); reject(new Error('video-decode-failed')); };
    const cleanup = () => {
      video.removeEventListener('loadedmetadata', onLoaded);
      video.removeEventListener('error', onError);
    };
    video.addEventListener('loadedmetadata', onLoaded, { once: true });
    video.addEventListener('error', onError, { once: true });
  });
}

/**
 * @param {File} file
 * @param {(progress:number)=>void} onProgress 0..1
 * @returns {Promise<File>} possibly-compressed file, or the original if
 *   compression isn't supported or the file is already small enough.
 */
export async function compressVideoClientSide(file, onProgress) {
  if (!file || file.size <= SIZE_THRESHOLD_BYTES) return file;

  const mime = pickSupportedMime();
  if (!mime) return file;

  let objectUrl = null;
  try {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.crossOrigin = 'anonymous';
    objectUrl = URL.createObjectURL(file);
    video.src = objectUrl;
    await waitForVideoReady(video);

    const srcW = video.videoWidth;
    const srcH = video.videoHeight;
    if (!srcW || !srcH) return file;

    const long = Math.max(srcW, srcH);
    const ratio = Math.min(1, TARGET_LONG_EDGE / long);
    // Even dimensions only, most codecs require that for yuv420p.
    const width = 2 * Math.round((srcW * ratio) / 2);
    const height = 2 * Math.round((srcH * ratio) / 2);

    // If the source is already small in resolution, skip canvas re-encode;
    // recording an identical copy adds little.
    if (ratio === 1 && file.size <= 20 * 1024 * 1024) return file;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { alpha: false, desynchronized: true });
    if (!ctx) return file;

    const canvasStream = canvas.captureStream(30);
    // Grab the source audio track if available.
    if (typeof video.captureStream === 'function') {
      try {
        const vs = video.captureStream();
        vs.getAudioTracks().forEach((t) => canvasStream.addTrack(t));
      } catch { /* noop */ }
    }

    const recorder = new MediaRecorder(canvasStream, {
      mimeType: mime,
      videoBitsPerSecond: TARGET_BITRATE,
      audioBitsPerSecond: 128_000,
    });
    const chunks = [];
    recorder.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data); };

    const duration = isFinite(video.duration) ? video.duration : 0;

    const done = new Promise((resolve, reject) => {
      recorder.onstop = () => resolve();
      recorder.onerror = (e) => reject(e.error || new Error('recorder-failed'));
    });

    // Draw loop, runs until the source video ends.
    let rafId = null;
    let stopped = false;
    const draw = () => {
      if (stopped) return;
      try { ctx.drawImage(video, 0, 0, width, height); } catch { /* noop */ }
      if (duration && typeof onProgress === 'function') {
        onProgress(Math.min(1, video.currentTime / duration));
      }
      rafId = requestAnimationFrame(draw);
    };

    await video.play();
    recorder.start(500); // flush every 500ms so we can stream chunks
    draw();

    await new Promise((resolve) => {
      video.addEventListener('ended', resolve, { once: true });
    });

    stopped = true;
    if (rafId) cancelAnimationFrame(rafId);
    recorder.stop();
    await done;

    const outBlob = new Blob(chunks, { type: mime });
    // Safety net, if the "compressed" output ended up larger than the input
    // (small or already-compressed source), bail and send the original.
    if (outBlob.size >= file.size) return file;

    const ext = mimeToExtension(mime);
    const base = file.name?.replace(/\.[^/.]+$/, '') || 'video';
    return new File([outBlob], `${base}.${ext}`, { type: mime });
  } catch {
    // Any failure => fall back to the raw upload. Server-side transcode
    // still enforces the target format.
    return file;
  } finally {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }
}
